import { useIntl } from "react-intl";
import labels from "./PrintPalletLabelCard.labels";
import { useRecoilValue, useResetRecoilState } from "recoil";
import { userState } from "@deliverr/ui-facility/lib-facility/base/Auth/userState";
import { usePrintZpl } from "@deliverr/ui-facility";
import { printPdf, isZebraScanner, log, logStart } from "@deliverr/ui-facility/lib-facility/utils";
import { crossdockClient } from "crossdock/base/Clients";
import { useState } from "react";
import { MAX_INBOUND_LABEL_COUNT } from "./printInboundPalletLabels.constants";
import { useScanFlow } from "crossdock/common/flow/useScanFlow";
import { useLoading } from "crossdock/common/useLoading";
import { useInputErrorMessage } from "crossdock/common/useInputErrorMessage";
import { usePrintInboundPalletLabelsNotification } from "./usePrintInboundPalletLabelsNotification";
import { asnIdState } from "./inboundPalletState";
import { createPrintInboundPalletLabelAsnCard } from "../InboundPalletLabelCardCreators";

const DEFAULT_PALLET_COUNT = "1";

export function usePrintPalletLabel() {
  const { successResponse, handleUnknownError } = useScanFlow();
  const { formatMessage } = useIntl();
  const { printZpl } = usePrintZpl();
  const { warehouseId } = useRecoilValue(userState);
  const [palletCount, setPalletCount] = useState<string>(DEFAULT_PALLET_COUNT);
  const { loading, loadWhilePending } = useLoading();
  const { inputErrorMessage, resetErrorOnExecution, inputError } = useInputErrorMessage();
  const { showInboundPalletLabelsGeneratedSuccessNotification } = usePrintInboundPalletLabelsNotification();
  const { addFlowCard } = useScanFlow();
  const asnId = useRecoilValue(asnIdState);
  const resetAsnId = useResetRecoilState(asnIdState);

  const updatePalletCount = (eventValue) => {
    setPalletCount(eventValue.target.value);
  };

  const generateAndPrintInboundPalletLabel = async (palletCountNum: number): Promise<void> => {
    const acceptHeader = isZebraScanner ? "application/zpl" : "application/pdf";
    const doc: string = atob(
      await crossdockClient.generateInboundAsnPalletLabels(acceptHeader, {
        asnId: Number(asnId),
        crossdockId: warehouseId,
        qty: palletCountNum,
      })
    );

    if (isZebraScanner) {
      await printZpl(atob(doc));
    } else {
      const blob = new Blob([Uint8Array.from(doc, (_, i) => doc.charCodeAt(i))], { type: acceptHeader });
      printPdf(blob);
    }

    resetAsnId();
    addFlowCard(createPrintInboundPalletLabelAsnCard({}));
  };

  const requestInboundPalletLabels = async (): Promise<void> => {
    const ctx = logStart({ fn: "requestInboundPalletLabels", palletCount, warehouseId });
    log(ctx, "getting inbound pallet label data");

    const palletCountNum = Number(palletCount);

    // shouldn't happen ever due to restriction in NumericInput
    if (Number.isNaN(palletCountNum)) {
      inputError(formatMessage(labels.notANumber));
      return;
    }

    if (palletCountNum > MAX_INBOUND_LABEL_COUNT) {
      inputError(formatMessage(labels.limitError, { maxLabelCount: MAX_INBOUND_LABEL_COUNT }));
      return;
    }

    if (palletCountNum <= 0) {
      inputError(formatMessage(labels.zeroNumberError));
      return;
    }

    try {
      await generateAndPrintInboundPalletLabel(palletCountNum);

      log(ctx, "generated inbound pallet label");
      successResponse();
      showInboundPalletLabelsGeneratedSuccessNotification(palletCountNum);
      setPalletCount(DEFAULT_PALLET_COUNT);
    } catch (e) {
      handleUnknownError(ctx, e);
    }
  };

  return {
    requestInboundPalletLabels: loadWhilePending(resetErrorOnExecution(requestInboundPalletLabels)),
    value: palletCount,
    palletCount: Number(palletCount),
    updatePalletCount,
    loading,
    inputErrorMessage,
  };
}
